/**
 * Booking client helpers.
 *
 * The mobile-ticket APIs were originally built for the Nomio mobile app, where
 * each device has a stable UUID stored in secure storage. On the web we mimic
 * that pattern by generating a UUID per browser and persisting it in
 * localStorage. The same `app_id` is reused across sessions so the user can
 * see their own active / historical tickets when they return.
 *
 * The `token` field expected by the backend is a bcrypt hash. The mobile app's
 * algorithm is replicated here verbatim so the backend's `bcrypt.compare` call
 * succeeds:
 *
 *   combinedString = `${branch_id}${category_id}${app_id}${req_date}`
 *   combinedParams = `${combinedString}${SECRET}`
 *   token          = bcrypt.hash(combinedParams, saltRounds = 3)
 *
 * The shared SECRET lives in `REACT_APP_BOOKING_TOKEN_SECRET` (set in the
 * project's `.env`). Without the correct secret the backend will return
 * "Invalid token" — keep the value in sync with the mobile app's `config.token`.
 */

// `bcrypt-ts` is a browser-first TypeScript port of `bcryptjs` with the
// exact same `hashSync` / `compareSync` API. We use it instead of the
// original `bcryptjs` because v2.x of that package emits a `require("crypto")`
// call that webpack 5 (CRA 5+) can't statically resolve, producing a noisy
// "Module not found: Can't resolve 'crypto'" warning on every dev build.
// `bcrypt-ts` ships with a clean browser bundle and avoids the warning
// without needing to add craco / react-app-rewired just for a polyfill.
import { hashSync, compareSync } from "bcrypt-ts";

const APP_ID_STORAGE_KEY = "nomio.booking.app_id";

/**
 * Shared bcrypt secret. Mirrors the mobile app's `config.token`. Read from the
 * env at module load (REACT_APP_* vars are inlined at build time by CRA).
 */
const TOKEN_SECRET = process.env.REACT_APP_BOOKING_TOKEN_SECRET || "";

/**
 * Salt rounds used for the ticket-token bcrypt hash. Matches the mobile app
 * (saltRounds = 3) so backend cost expectations are stable.
 */
const TOKEN_SALT_ROUNDS = 3;

/**
 * Generate a v4-shaped UUID. Falls back to a Math.random based generator on
 * older browsers where `crypto.randomUUID` is unavailable.
 */
function generateUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch (_err) {
      // ignore and fall through
    }
  }
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0"));
    return (
      `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-` +
      `${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-` +
      hex.slice(10).join("")
    );
  }
  // Last-resort fallback (not a real UUID, but stable per-call format).
  const rand = () => Math.random().toString(16).slice(2, 10);
  return `${rand()}-${rand().slice(0, 4)}-4${rand().slice(0, 3)}-` +
    `8${rand().slice(0, 3)}-${rand()}${rand().slice(0, 4)}`;
}

/**
 * Returns a stable, persisted UUID for this browser. Used as the `app_id`
 * sent to all mobile-ticket APIs.
 */
export function getOrCreateAppId() {
  try {
    let id = localStorage.getItem(APP_ID_STORAGE_KEY);
    if (!id) {
      id = generateUuid();
      localStorage.setItem(APP_ID_STORAGE_KEY, id);
    }
    return id;
  } catch (_err) {
    // localStorage unavailable (private mode, SSR) — fall back to
    // ephemeral per-call id so the request still succeeds.
    return generateUuid();
  }
}

/**
 * Reset the persisted app_id. Useful from "forget device" UIs or testing.
 */
export function resetAppId() {
  try {
    localStorage.removeItem(APP_ID_STORAGE_KEY);
  } catch (_err) {
    /* noop */
  }
}

/**
 * Build the canonical "combined string" hashed by the mobile app.
 *
 * Mobile app source:
 *   const combinedString = branchId + '' + categoryId + '' + uniqueId + '' + date;
 *
 * The empty-string concatenations are kept verbatim so the wire format is
 * byte-identical to the mobile flow (no separators between fields).
 */
function buildCombinedString({ branch_id, category_id, app_id, req_date }) {
  return `${branch_id}${category_id}${app_id}${req_date}`;
}

function hashParameters(parameters) {
  return hashSync(`${parameters}${TOKEN_SECRET}`, TOKEN_SALT_ROUNDS);
}

/**
 * Generate the bcrypt `token` field for a ticket-create request.
 *
 * Replicates the mobile app's `hashParameters` helper:
 *   combinedString = branchId + categoryId + appId + reqDate (no separators)
 *   combinedParams = combinedString + SECRET
 *   token          = bcrypt.hash(combinedParams, saltRounds = 3)
 *
 * The secret comes from `REACT_APP_BOOKING_TOKEN_SECRET`. If it's empty the
 * hash will still be generated (so dev flows don't crash), but the backend
 * will reject the request — surface a console warning so misconfiguration
 * is obvious during development.
 */
export function generateTicketToken({ branch_id, category_id, app_id, req_date }) {
  if (!TOKEN_SECRET && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[booking] REACT_APP_BOOKING_TOKEN_SECRET is empty — ticket token will be rejected by the backend.",
    );
  }
  const combinedString = buildCombinedString({
    branch_id,
    category_id,
    app_id,
    req_date,
  });
  const combinedParams = `${combinedString}${TOKEN_SECRET}`;
  const token = hashSync(combinedParams, TOKEN_SALT_ROUNDS);

  if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.debug("[booking] token-input", {
      combinedString,
      combinedStringLength: combinedString.length,
      secretLength: TOKEN_SECRET.length,
      secretPreview: TOKEN_SECRET
        ? `${TOKEN_SECRET.slice(0, 2)}…${TOKEN_SECRET.slice(-2)}`
        : "(empty)",
      saltRounds: TOKEN_SALT_ROUNDS,
      tokenPrefix: token.slice(0, 7),
    });
  }

  return token;
}

export function generateRatingToken({
  locale,
  app_id,
  order_ticket_id,
  rating,
}) {
  if (!TOKEN_SECRET && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[booking] REACT_APP_BOOKING_TOKEN_SECRET is empty - rating token will be rejected by the backend.",
    );
  }
  const combinedString = `${locale}${app_id}${order_ticket_id}${rating}`;
  return hashParameters(combinedString);
}

export function generateTicketsToken({ locale, app_id }) {
  if (!TOKEN_SECRET && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[booking] REACT_APP_BOOKING_TOKEN_SECRET is empty - tickets token will be rejected by the backend.",
    );
  }
  const combinedString = `${locale}${app_id}`;
  if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.debug("[booking] tickets-token-input", {
      locale,
      appIdLength: String(app_id || "").length,
      secretLength: TOKEN_SECRET.length,
      combinedStringLength: combinedString.length,
      combinedWithSecretLength: `${combinedString}${TOKEN_SECRET}`.length,
    });
  }
  return hashParameters(combinedString);
}

export function generateCancelTicketToken({ locale, order_id, app_id }) {
  if (!TOKEN_SECRET && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[booking] REACT_APP_BOOKING_TOKEN_SECRET is empty - cancel token will be rejected by the backend.",
    );
  }
  const combinedString = `${locale}${order_id}${app_id}`;
  if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.debug("[booking] cancel-token-input", {
      locale,
      orderId: order_id,
      appIdLength: String(app_id || "").length,
      secretLength: TOKEN_SECRET.length,
      combinedStringLength: combinedString.length,
      combinedWithSecretLength: `${combinedString}${TOKEN_SECRET}`.length,
    });
  }
  return hashParameters(combinedString);
}

export function generateDelayTicketToken({ locale, order_id, minute }) {
  if (!TOKEN_SECRET && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[booking] REACT_APP_BOOKING_TOKEN_SECRET is empty - delay token will be rejected by the backend.",
    );
  }
  const combinedString = `${locale}${order_id}${minute}`;
  if (process.env.NODE_ENV !== "production" && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.debug("[booking] delay-token-input", {
      locale,
      orderId: order_id,
      minute,
      secretLength: TOKEN_SECRET.length,
      combinedStringLength: combinedString.length,
      combinedWithSecretLength: `${combinedString}${TOKEN_SECRET}`.length,
    });
  }
  return hashParameters(combinedString);
}

/**
 * Browser-console helper for verifying a known-good bcrypt token against
 * our algorithm. Usage from DevTools:
 *
 *   window.__bookingTokenDebug.verify({
 *     branch_id: 29,
 *     category_id: 153,
 *     app_id: "306233a5-48ca-4c1d-ab3c-809812d2f7a4",
 *     req_date: "2026-06-08 13:08",
 *     token: "$2a$04$aE7sQZgCPNHsoUVW2AEQ3.TEJpafARVon8mmzsZY19nsfTArE.EJW",
 *   })
 *
 * Returns `true` when our `combinedString + SECRET` matches the supplied
 * token — i.e. our algorithm + secret are correct.
 */
if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
  window.__bookingTokenDebug = {
    secretLength: TOKEN_SECRET.length,
    saltRounds: TOKEN_SALT_ROUNDS,
    build: ({ branch_id, category_id, app_id, req_date }) =>
      `${buildCombinedString({ branch_id, category_id, app_id, req_date })}${TOKEN_SECRET}`,
    hash: (params) => generateTicketToken(params),
    verify: ({ branch_id, category_id, app_id, req_date, token }) => {
      const input = `${buildCombinedString({ branch_id, category_id, app_id, req_date })}${TOKEN_SECRET}`;
      return compareSync(input, token);
    },
  };
}

/**
 * Format a Date (or timestamp ms) into the `YYYY-MM-DD HH:mm` shape
 * expected by `mobile-ticket/create`.
 *
 * The mobile app and backend canonical form is `YYYY-MM-DD HH:mm` (no
 * seconds). Even though `get_mobile_order_time_suggestions` returns
 * `2026-06-13 09:00:00`, the create-ticket request must drop the seconds
 * — the backend stores them itself (as `:00`) and re-hashes the input
 * without seconds when verifying the bcrypt token. Sending seconds
 * therefore breaks token verification on some backend code paths.
 *
 * Strings already shaped as `YYYY-MM-DD HH:mm[:ss]` are normalized to
 * `HH:mm` so callers can pass raw API timestamps (`slot.raw`) directly.
 */
export function formatReqDate(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    const match = trimmed.match(
      /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?$/,
    );
    if (match) {
      return `${match[1]} ${match[2]}`;
    }
  }
  const d = value instanceof Date ? value : new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Map a ui i18n locale (`hy`, `en`, `ru`) to the value the ticket API stores
 * in its `lang` field. Same mapping as `getSecondApiLocale` (hy → am).
 */
export function getTicketLang(lang) {
  const normalized = (lang || "").toLowerCase();
  if (normalized === "hy") return "am";
  if (normalized === "ru") return "ru";
  return "en";
}
